import type { CrmChatProviderKey } from '@/api/types/crm/crmLeadFunnels.types'

export const CRM_INTEGRATION_CHAT_DEFAULT_PER_PAGE = 20
export const CRM_INTEGRATION_CHAT_MESSAGES_DEFAULT_LIMIT = 10
export const CRM_INTEGRATION_CHAT_MESSAGES_MAX_LIMIT = 200

/** GET /v1/crm-integration-chat/unread-count — элемент integrations[] */
export type CrmIntegrationChatUnreadIntegration = {
  integration_id: number
  provider: CrmChatProviderKey | string
  name: string
  /** Все непрочитанные (сообщения) по интеграции */
  count: number
  /** Непрочитанные по лидам текущего менеджера */
  my_count?: number
  /** Кол-во чатов с непрочитанными (все) */
  unread_chats_count?: number
  /** Кол-во непрочитанных по диалогам лидов текущего менеджера */
  my_unread_chats_count?: number
}

/** GET /v1/crm-integration-chat/unread-count */
export type CrmIntegrationChatUnreadCountData = {
  total: number
  /** Сумма my_* по интеграциям (лиды текущего менеджера) */
  my_total?: number
  integrations: CrmIntegrationChatUnreadIntegration[]
}

/** @deprecated legacy crm-chat/unread-count */
export type CrmChatUnreadCountData = {
  count: number
}

/** GET /v1/crm-integration-chat и блок conversation в messages */
export type CrmIntegrationConversation = {
  id: number
  integration_id: number
  provider: CrmChatProviderKey | string
  /** Wazzup: id канала во внешней системе (фильтр списка на клиенте). */
  external_channel_id?: string | null
  /** Wazzup: тип канала (whatsapp / vk / telegram / …). */
  external_chat_type?: string | null
  external_recipient: string
  external_user_id: string | null
  lead_id: number | null
  title: string | null
  last_message_id: string | null
  last_message_text: string | null
  unread_count: number
  created_at: string | null
  updated_at: string | null
  crm_access?: {
    is_accessible: boolean
    role_access?: boolean
    department_access?: boolean
  } | null
}

/** @deprecated use CrmIntegrationConversation */
export type CrmConversationListItem = CrmIntegrationConversation

/** GET /v1/crm-integration-chat — `_meta` */
export type CrmIntegrationChatListMeta = {
  totalCount: number
  pageCount: number
  /** Новый API: `page`; legacy chats: `currentPage` */
  page?: number
  currentPage?: number
  perPage: number
}

/** GET /v1/crm-integration-chat — `data` */
export type CrmIntegrationChatListData = {
  items: CrmIntegrationConversation[]
  _meta: CrmIntegrationChatListMeta
}

export type CrmIntegrationChatGetChatsParams = {
  page?: number
  perPage?: number
  integrationId?: number
  /** Wazzup / channel-провайдеры: internal channel id или external_channel_id */
  channelId?: string | number | null
  /** user_id менеджера; `0` = лиды без ответственного */
  managerId?: number | null
  search?: string
  provider?: CrmChatProviderKey | string
  leadId?: number
  conversationId?: number
  /** Не показывать глобальный тост (resolve вкладок и т.п.) */
  skipGlobalErrorAlert?: boolean
}

/** GET /v1/crm-integration-chat?provider=&lead_id= — доп. поля сводки диалога */
export type CrmIntegrationConversationWithMeta = CrmIntegrationConversation & {
  can_send?: boolean
  send_block_code?: string | null
  send_block_reason?: string | null
  profile_url?: string | null
  vk_link?: string | null
  lead_name?: string | null
  link_by_id?: string | null
}

export type CrmIntegrationChatListSelection = {
  conversationId: number
  leadId: number | null
  integrationId: number
  provider: string
}

export type CrmIntegrationMessageDeliveryStatus = 'sent' | 'failed' | 'pending'

export type CrmIntegrationMessageDelivery = {
  status: CrmIntegrationMessageDeliveryStatus
  error: string | null
  delivered_at: string | null
  queue_job_id: number | null
}

export type CrmIntegrationAttachmentKind =
  | 'image'
  | 'file'
  | 'video'
  | 'audio'
  | 'voice'
  | 'animation'
  | 'unknown'

export type CrmIntegrationAttachmentProvider =
  | 'vkontakte'
  | 'telegram'
  | 'max'
  | 'wazzup'

export type CrmIntegrationAttachmentProviderMeta = Record<string, unknown>

/** Вложение VK / TG / MAX / Wazzup — унифицированный контракт бэка */
export type CrmIntegrationAttachment = {
  kind: CrmIntegrationAttachmentKind
  /** Основной URL для `<img src>` — бэк всегда резолвит для истории */
  url: string | null
  preview_url: string | null
  /** Исходящие: id в user_files; url собирается на бэке из path */
  user_file_id?: number | null
  name: string | null
  mime: string | null
  size: number | null
  duration: number | null
  /** У Wazzup часто отсутствует */
  provider: CrmIntegrationAttachmentProvider | null
  provider_meta?: CrmIntegrationAttachmentProviderMeta
}

/** GET /v1/crm-integration-chat/messages — элемент ленты (VK / TG / MAX) */
export type CrmIntegrationMessageSender = {
  type: string
  id: number
  name: string
}

export type CrmIntegrationMessage = {
  id: number
  conversation_id: number
  integration_id: number
  direction: 'incoming' | 'outgoing'
  text: string
  message_text: string
  is_read: 0 | 1
  created_at: string
  updated_at: string
  attachments: CrmIntegrationAttachment[] | unknown[]
  delivery: CrmIntegrationMessageDelivery
  delivery_status?: string
  delivery_error?: string | null
  delivered_at?: string | null
  provider_message_id?: string | null
  external_message_id?: string | null
  /** Автор исходящего (пользователь платформы); у входящих обычно null */
  sender?: CrmIntegrationMessageSender | null
}

/** GET /v1/crm-integration-chat/messages — `data` */
export type CrmIntegrationMessagesResponse = {
  conversation: CrmIntegrationConversation
  items: CrmIntegrationMessage[]
}

/** Alias для совместимости с существующими импортами */
export type CrmIntegrationChatMessagesData = CrmIntegrationMessagesResponse

/** @deprecated use CrmIntegrationConversation */
export type CrmIntegrationChatConversation = CrmIntegrationConversation

export type CrmIntegrationChatGetMessagesParams = {
  conversation_id: number
  limit?: number
  offset?: number
}

export type CrmIntegrationOutgoingAttachmentKind =
  | 'image'
  | 'file'
  | 'video'
  | 'audio'
  | 'voice'
  | 'animation'

/** POST /v1/crm-integration-chat/send — исходящее вложение */
export type CrmIntegrationOutgoingAttachment = {
  kind: CrmIntegrationOutgoingAttachmentKind
  url?: string
  user_file_id?: number
  name?: string
  mime?: string
  size?: number
  preview_url?: string
  duration?: number
}

/** POST /v1/crm-integration-chat/send */
export type CrmIntegrationChatSendRequest = {
  conversation_id: number
  text?: string
  /** Алиас поля `text` на бэке */
  message?: string
  attachments?: CrmIntegrationOutgoingAttachment[]
}

/** POST /v1/crm-integration-chat/mark-read */
export type CrmIntegrationChatMarkReadRequest = {
  conversation_id: number
}

export type CrmIntegrationChatMarkReadResponse = {
  conversation_id: number
}

/** DELETE /v1/crm-integration-chat/delete-message */
export type CrmIntegrationChatDeleteMessageResponse = {
  message_id: number
  deleted: boolean
}

/** POST /v1/crm-integration-chat/send — успех (200) */
export type CrmIntegrationSendResponse = {
  status: 'sent' | 'queued'
  message_id: number
  provider_result: Record<string, unknown>
}

/** Phase 4 — GET /v1/crm-integration-chat/conversation (planned) */
export type CrmIntegrationConversationSummary = {
  conversation_id: number
  provider: string
  integration_id: number
  title: string | null
  recipient: string
  unread_count: number
  can_send: boolean
  send_block_code: string | null
  send_block_reason: string | null
  profile_url: string | null
}
